/**
 * arbi download [doc-id] [--output <path>]
 *
 * Interactive doc picker if no doc-id given.
 */

import { Command } from 'commander'
import fs from 'node:fs'
import path from 'node:path'
import { documents } from '@arbidocs/sdk'
import { runAction, resolveWorkspace } from '../helpers.js'
import { success } from '../output.js'
import { promptSearch } from '../prompts.js'

export function registerDownloadCommand(program: Command): void {
  program
    .command('download [doc-id]')
    .description('Download a document (interactive picker if no ID given)')
    .option(
      '-o, --output <path>',
      'Output file path. Use "-" to stream raw bytes to stdout (for piping).'
    )
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option(
      '--json',
      'Emit a JSON metadata line to stdout after writing the file (doc_id, path, bytes, filename). Implies a file write — use "-o -" or pipe stdout to get bytes without JSON.'
    )
    .action(
      (docId: string | undefined, opts: { output?: string; workspace?: string; json?: boolean }) =>
        runAction(async () => {
          const { arbi, config, accessToken } = await resolveWorkspace(opts.workspace)

          // Pick doc if not given
          if (!docId) {
            const data = await documents.listDocuments(arbi)
            if (data.length === 0) {
              console.log('No documents found.')
              return
            }
            const choices = data.map((d) => ({
              name: `${d.file_name ?? 'Unnamed'} (${d.status})`,
              value: d.external_id as string,
              description: d.external_id as string,
            }))
            docId = await promptSearch('Select document to download', choices)
          }

          const res = await documents.downloadDocument(
            { baseUrl: config.baseUrl, accessToken },
            docId
          )

          let filename = `${docId}`
          const disposition = res.headers.get('content-disposition')
          if (disposition) {
            const match = disposition.match(/filename[*]?=(?:UTF-8''|"?)([^";]+)/i)
            if (match) filename = decodeURIComponent(match[1].replace(/"/g, ''))
          }

          const buffer = Buffer.from(await res.arrayBuffer())

          // Stream to stdout if -o -
          if (opts.output === '-') {
            process.stdout.write(buffer)
            return
          }

          const outputPath = opts.output || path.join(process.cwd(), filename)
          fs.writeFileSync(outputPath, buffer)

          if (opts.json) {
            console.log(
              JSON.stringify({
                doc_id: docId,
                path: outputPath,
                bytes: buffer.length,
                filename: path.basename(outputPath),
              })
            )
            return
          }

          success(
            `Downloaded: ${path.basename(outputPath)} (${(buffer.length / (1024 * 1024)).toFixed(1)} MB)`
          )
        })()
    )
}
